import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";

import { API_EXTENSION_REPORT_SUMMARY } from "../../../../../config/Endpoints.js";
import { showError } from "../../../../utils/toast.ts";
import type {
  EmployeeExtensionSummary,
  ExtensionSummaryPage,
  ExtensionReportFilters,
} from "../types.ts";

function buildParams(
  filters: ExtensionReportFilters,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (filters.employeeId != null) params.employeeId = filters.employeeId;
  if (filters.fromDate) params.fromDate = filters.fromDate;
  if (filters.toDate) params.toDate = filters.toDate;
  if (filters.status) params.status = filters.status;
  return params;
}

function getApiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message;
    if (typeof msg === "string" && msg.trim() !== "") return msg;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function useExtensionReport(
  filters: ExtensionReportFilters,
  page: number,
  pageSize: number,
) {
  const [data, setData] = useState<EmployeeExtensionSummary[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const token = Cookies.get("accessToken");

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<ExtensionSummaryPage | EmployeeExtensionSummary[]>(
        API_EXTENSION_REPORT_SUMMARY,
        {
          params: {
            ...buildParams(filters),
            page: Math.max(0, page - 1),
            size: pageSize,
          },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const body = res.data;
      if (Array.isArray(body)) {
        setData(body);
        setTotalElements(body.length);
      } else {
        setData(Array.isArray(body.content) ? body.content : []);
        setTotalElements(Number(body.totalElements) || 0);
      }
    } catch (err) {
      const msg = getApiErrorMessage(
        err,
        "Failed to load extension report.",
      );
      setData([]);
      setTotalElements(0);
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, token]);

  useEffect(() => {
    void fetchReport();
  }, [fetchReport]);

  return { data, totalElements, loading, error, refetch: fetchReport };
}
